function safeFileName(value) {
  return String(value || "informe-dxa")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLocaleLowerCase("es") || "informe-dxa";
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportRecordJson(record) {
  const name = safeFileName(record.patient?.name);
  const blob = new Blob([JSON.stringify(record, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  downloadBlob(blob, `${name}-dxa.json`);
}

export function exportReportHtml(record) {
  const report = document.querySelector("#patient-report");
  if (!report) throw new Error("No se encontró la vista del informe.");
  const styleText = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText).join("\n");
      } catch {
        return "";
      }
    })
    .join("\n");
  const title = `Informe DXA · ${record.patient?.name || "Paciente"}`;
  const html = `<!doctype html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${title.replace(/[<>&"]/g, "")}</title>
  <style>${styleText}</style>
</head>
<body class="exported-report">
${report.outerHTML}
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  downloadBlob(blob, `${safeFileName(record.patient?.name)}-informe-dxa.html`);
}

function waitForReportImages(root) {
  return Promise.all(Array.from(root.querySelectorAll("img")).map((image) => {
    if (image.complete && image.naturalWidth > 0) return Promise.resolve();
    if (typeof image.decode === "function") return image.decode().catch(() => undefined);
    return new Promise((resolve) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", resolve, { once: true });
    });
  }));
}

function pageOrientation(width, height) {
  return width > height ? "landscape" : "portrait";
}

function addPageLinks(pdf, page, pageNumber, width, height) {
  const bounds = page.getBoundingClientRect();
  const scaleX = width / Math.max(page.scrollWidth, 1);
  const scaleY = height / Math.max(page.scrollHeight, 1);

  page.querySelectorAll('a[href^="http"]').forEach((anchor) => {
    const rect = anchor.getBoundingClientRect();
    const x = Math.max(0, (rect.left - bounds.left) * scaleX);
    const y = Math.max(0, (rect.top - bounds.top) * scaleY);
    const linkWidth = Math.min(width - x, rect.width * scaleX);
    const linkHeight = Math.min(height - y, rect.height * scaleY);
    if (linkWidth <= 0 || linkHeight <= 0) return;
    pdf.setPage(pageNumber);
    pdf.link(x, y, linkWidth, linkHeight, { url: anchor.href });
  });
}

export async function exportReportPdf(record, onProgress = () => undefined) {
  const report = document.querySelector("#patient-report");
  if (!report) throw new Error("No se encontró la vista del informe.");

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);

  const stage = document.createElement("div");
  stage.className = "pdf-export-stage";
  stage.setAttribute("aria-hidden", "true");
  const reportClone = report.cloneNode(true);
  reportClone.id = "patient-report-pdf-clone";
  stage.appendChild(reportClone);
  document.body.appendChild(stage);

  try {
    await document.fonts?.ready;
    await waitForReportImages(reportClone);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const pages = Array.from(reportClone.querySelectorAll(".dxa-report-page, .report-original-page"));
    if (!pages.length) throw new Error("El informe no contiene páginas para exportar.");

    let pdf = null;
    const linkTasks = [];

    for (let index = 0; index < pages.length; index += 1) {
      const page = pages[index];
      const width = Math.ceil(page.scrollWidth);
      const height = Math.ceil(page.scrollHeight);
      onProgress({ current: index + 1, total: pages.length });

      const canvas = await html2canvas(page, {
        backgroundColor: "#ffffff",
        imageTimeout: 5000,
        logging: false,
        removeContainer: true,
        scale: 1.5,
        useCORS: true,
        width,
        height,
        windowWidth: 1200,
        windowHeight: Math.max(1200, height),
      });

      const orientation = pageOrientation(width, height);
      if (!pdf) {
        pdf = new jsPDF({
          orientation,
          unit: "px",
          format: [width, height],
          compress: true,
          hotfixes: ["px_scaling"],
        });
      } else {
        pdf.addPage([width, height], orientation);
      }

      const pageImage = canvas.toDataURL("image/jpeg", 0.98);
      pdf.addImage(pageImage, "JPEG", 0, 0, width, height, undefined, "FAST");
      linkTasks.push({ page, pageNumber: index + 1, width, height });
      canvas.width = 1;
      canvas.height = 1;
    }

    linkTasks.forEach(({ page, pageNumber, width, height }) => {
      addPageLinks(pdf, page, pageNumber, width, height);
    });
    pdf.setProperties({
      title: `Informe DXA · ${record.patient?.name || "Paciente"}`,
      subject: "Interpretación profesional de evaluación DXA",
      author: record.professional?.approvedBy || "Nutricionista Paola Hoyos",
      creator: "Sistema de informes DXA",
    });
    pdf.save(`${safeFileName(record.patient?.name)}-informe-dxa.pdf`);
  } finally {
    stage.remove();
  }
}
